import { findPageTreeMatches } from '../../../helpers/pageTreeSearch';
import { useCacheRefresh } from "../../components/common/CacheRefreshProvider";
import { faCircleNotch, faGlobe, faLanguage, faList, faMinus, faPenToSquare, faPlus, faRetweet, faTags, faTimes } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import React, { forwardRef, useEffect, useImperativeHandle, useMemo, useRef, useState } from "react";
import { NodeApi, NodeRendererProps, Tree, TreeApi } from "react-arborist";
import { Button, DropdownItem, FormControl } from "react-bootstrap";
import { ContentService } from "../../../services/ContentService";
import { TreeHelper } from "../../../helpers/TreeHelper";
import { IActionResult, IArboristTreeNode } from "../../../models/Interfaces";
import AppModal from "../../components/common/AppModal";
import { EventBus } from "../../../helpers/EventBus";
import { useNavigate } from "react-router-dom";
import { useConfirm } from "../../../config/ConfirmProvider";
import { SiteService } from "../../../services/SiteService";
import styles from "./contentHome.module.scss";
import { actionEmitter, EmitEvent } from "../../../config/ActionEmitter";
import AddPageDialogue, { IAddPageDialogueRef } from "./AddPageDialogue";

interface IPagesTree {
  beforePageSelect?: (pageid: string) => Promise<boolean>;
  onPageSelected?: (pageid: string | null) => void;
  OnSelectedAction?: (pageid: string | null) => void;
  onPageCountChange?: (count: number) => void;
}

export interface ITreeCompRef {
  getSitePages: (pageid: number | undefined) => Promise<void>;
  selectPage: (pageid: string) => void;
}

const PagesTree = forwardRef<ITreeCompRef, IPagesTree>((props, ref) => {
  const cacheRefresh = useCacheRefresh();
  const heightdiff: number = 280;
  const [pageData, setPageData] = useState<IArboristTreeNode[]>([]);
  const [treeHeight, setTreeHeight] = useState<number>(window.innerHeight - heightdiff);
  const [selectedNodeId, setSelectedNodeId] = useState<string | null>(null);
  const selectedNodeIdRef = useRef(selectedNodeId);
  selectedNodeIdRef.current = selectedNodeId;
  const [selectedNode, setSelectedNode] = useState<IArboristTreeNode | null>(null);
  const [ctxMenu, setCtxMenu] = useState<{ x: number; y: number; nodeId: string | null }>({ x: 0, y: 0, nodeId: null });
  const [loadingPages, setLoadingPages] = useState(true);
  const pendingPageLoads = useRef(0);
  const [reloadingTree, setReloadingTree] = useState(false);
  const reloadInProgress = useRef(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [matchIndex, setMatchIndex] = useState(0);
  const matchIds = useMemo(() => findPageTreeMatches(pageData, searchQuery), [pageData, searchQuery]);
  const currentMatch = matchIds[matchIndex % (matchIds.length || 1)];
  const [showAddPage, setShowAddPage] = useState<boolean>(false);

  const treeRef = useRef<TreeApi<IArboristTreeNode>>(null);
  const compRef = useRef<any>(null);
  const contentSvcRef = useRef<ContentService | null>(null);
  const siteSvcRef = useRef<SiteService | null>(null);

  const ctxMenuRef = useRef<HTMLDivElement | null>(null);
  const treeBodyRef = useRef<HTMLDivElement | null>(null);
  const addPageParentRef = useRef<IArboristTreeNode | null>(null);
  const addPageRef = useRef<IAddPageDialogueRef | null>(null);

  const nav = useNavigate();
  const confirmation = useConfirm();

  const actionResultHandler = async (result: IActionResult): Promise<void> => {
    if (result?.Success) {
      const pageid = isNaN(Number(result?.Data)) ? null : Number(result.Data);
      await getSitePages();
      setShowAddPage(false);
      if (pageid && props.beforePageSelect && !await props.beforePageSelect(pageid.toString())) return;
      setSelectedNodeId(pageid?.toString() ?? "");

      if (props.onPageSelected) {
        props.onPageSelected(pageid?.toString() ?? "");
      }
      setShowAddPage(false);
    }
  };

  const addPage = async (): Promise<void> => {
    if (ctxMenu.nodeId && treeRef.current) {
      const nodeApi = treeRef.current.get(ctxMenu.nodeId);
      addPageParentRef.current = nodeApi ? nodeApi.data : null;
    } else {
      addPageParentRef.current = selectedNode;
    }
    setShowAddPage(true);
  };

  const createMenu = () => {
    const pageSelected: boolean = !(isNaN(Number(ctxMenu.nodeId ?? "NONUM")));

    return (
      <div
        className={`dropdown-menu ${styles.contextMenu}`}
        ref={ctxMenuRef}
        style={{
          display: 'flex',
          position: 'absolute',
          top: ctxMenu.y,
          left: ctxMenu.x,
          zIndex: 1080,
        }}
        onMouseLeave={handleCloseMenu}
      >
        <div className="vstack">
          <DropdownItem disabled={!cacheRefresh.canRefresh} onClick={() => { void cacheRefresh.refresh(); setCtxMenu({ x: 0, y: 0, nodeId: null }); }}>
            <FontAwesomeIcon icon={faRetweet} className="me-2" />
            <span>Refresh published pages</span>
          </DropdownItem>
          <DropdownItem onClick={addPage}>
            <FontAwesomeIcon icon={faPlus} className="me-2" />
            <span>Add a page</span>
          </DropdownItem>
          <DropdownItem hidden={!pageSelected} onClick={() => addPageVersion(ctxMenu.nodeId)}>
            <FontAwesomeIcon icon={faList} className="me-2" />
            <span>Add a new version</span>
          </DropdownItem>
          <DropdownItem hidden={!pageSelected} onClick={() => editKeyword(ctxMenu.nodeId)}>
            <FontAwesomeIcon icon={faTags} className="me-2" />
            <span>Edit keywords</span>
          </DropdownItem>
          <DropdownItem hidden={!pageSelected} onClick={() => editMetaData(ctxMenu.nodeId)}>
            <FontAwesomeIcon icon={faPenToSquare} className="me-2" />
            <span>Edit meta tags</span>
          </DropdownItem>
          <DropdownItem hidden={!pageSelected} onClick={editRedirects}>
            <FontAwesomeIcon icon={faRetweet} className="me-2" />
            <span>Redirects</span>
          </DropdownItem>
          <DropdownItem onClick={() => deletePage(ctxMenu.nodeId)} hidden={!pageSelected}>
            <FontAwesomeIcon icon={faTimes} className="me-2" />
            <span>Delete page</span>
          </DropdownItem>
        </div>
      </div>
    );
  };

  const handlePopupMenu = async (event: React.MouseEvent, node: IArboristTreeNode): Promise<void> => {
    event.preventDefault();
    event.stopPropagation();

    const treeBody = treeBodyRef.current;
    if (!treeBody) {
      return;
    }

    const rect = treeBody.getBoundingClientRect();
    const menuWidth = 240;
    const menuHeight = 300;
    const maxX = treeBody.clientWidth - menuWidth - 8;
    const maxY = treeBody.clientHeight + treeBody.scrollTop - menuHeight - 8;
    const x = Math.max(8, Math.min(event.clientX - rect.left + treeBody.scrollLeft, maxX));
    const y = Math.max(8, Math.min(event.clientY - rect.top + treeBody.scrollTop, maxY));

    if (treeRef.current) {
      const nodeApi = treeRef.current.get(node.id);
      if (nodeApi && !nodeApi.isSelected) {
        if (!await handleNodeClick(nodeApi)) return;
      }
    }

    setCtxMenu({ x, y, nodeId: node.id });

    if (ctxMenuRef.current) {
      ctxMenuRef.current.classList.add('show');
    }
  };

  const handleNodeClick = async (node: NodeApi<IArboristTreeNode>): Promise<boolean> => {
    if (node) {
      if (props.beforePageSelect && !await props.beforePageSelect(node.data.id)) {
        if (selectedNodeIdRef.current) treeRef.current?.get(selectedNodeIdRef.current)?.select();
        return false;
      }
      setSelectedNodeId(node.data.id);
      setSelectedNode(node.data);
      if (props.onPageSelected) {
        props.onPageSelected(node.data.id);
      }
    }
    return true;
  };

  const handleCloseMenu = (event: React.MouseEvent): void => {
    const menuElement = ctxMenuRef.current;

    // Check if relatedTarget is a Node and if it exists
    if (menuElement && event.relatedTarget instanceof Node) {
      // Check if the related target is within the menu
      if (!menuElement.contains(event.relatedTarget)) {
        // If the mouse leaves the menu, close it
        setCtxMenu({ x: 0, y: 0, nodeId: null });
        ctxMenuRef.current?.classList.remove('show');
      }
    } else {
      // Handle the case where relatedTarget is not a Node (like when clicking outside the window)
      setCtxMenu({ x: 0, y: 0, nodeId: null });
      ctxMenuRef.current?.classList.remove('show');
    }
  };

  const expandTreeToNode = (pageid: string): void => {
    if (treeRef.current) {
      const node = treeRef.current.get(pageid);

      if (node) {
        let parent = node.parent;

        while (parent) {
          parent.open();
          parent = parent.parent;
        }
      }
    }
  };

  const getSitePages = async (pageid?: number): Promise<void> => {
    if (contentSvcRef.current) {
      pendingPageLoads.current++;
      setLoadingPages(true);
      try {
        const pages = await contentSvcRef.current.getPagesTreeData();
        props.onPageCountChange?.(pages.length);

        if (pages.length > 0) {
          if (treeRef.current) {
            const sorted = pages.sort((a, b) => a.text.localeCompare(b.text));
            setPageData(TreeHelper.buildTreeDataH(sorted));
            if (pageid && !isNaN(Number(pageid))) {
              setSelectedNodeId(pageid.toString());
            }
          }
        }
        if (pages.length === 0) {
          setPageData([]);
          setSelectedNodeId(null);
          setSelectedNode(null);
        }
      } finally {
        pendingPageLoads.current--;
        if (pendingPageLoads.current === 0) setLoadingPages(false);
      }
    }
  };

  const reloadPageTree = async (): Promise<void> => {
    if (reloadInProgress.current) return;
    reloadInProgress.current = true;
    setReloadingTree(true);
    try {
      await getSitePages();
    } catch {
      actionEmitter.emit(EmitEvent.Result, false, 'Unable to reload the page tree. Please try again.');
    } finally {
      reloadInProgress.current = false;
      setReloadingTree(false);
    }
  };

  const removePageNode = (nodes: IArboristTreeNode[], pageid: string): IArboristTreeNode[] => {
    return nodes
      .filter((node) => node.id !== pageid)
      .map((node) => ({
        ...node,
        children: removePageNode(node.children ?? [], pageid)
      }));
  };

  const selectPage = (pageid: string): void => {
    const node = treeRef.current?.get(pageid);
    if (node) void handleNodeClick(node);
  };

  const handlePageSave = async (): Promise<void> => {
    if (addPageRef.current) {
      const res = await addPageRef.current.savePage();
      if (res) {
        actionResultHandler(res);
      }
    }
  };

  const nodeRender = (node: NodeRendererProps<IArboristTreeNode>) => {
    const nodeselected: boolean = node.node.isSelected;
    const cls = nodeselected ? `${styles.treeNode} ${styles.treeNodeSelected}` : styles.treeNode;
    const haschildren = ((node.node.children?.length ?? 0) > 0) || ((node.node.data.children?.length ?? 0) > 0);
    const getTypeIcon = (type: string): any => {
      let ico = <FontAwesomeIcon icon={faGlobe} className="text-primary me-1" />

      if (type.toLowerCase() === "lang") {
        ico = <FontAwesomeIcon icon={faLanguage} className="text-primary me-1" />
      }

      return ico;
    }

    return (
      <div style={node.style} ref={node.dragHandle} className={`app-tree-row ${cls}`}
        onClick={() => handleNodeClick(node.node)}
        onContextMenu={(event: React.MouseEvent) => handlePopupMenu(event, node.node.data)}>
        <div className={styles.treeNodeToggle}>
          {!haschildren && (
            <div className="hstack">
              <FontAwesomeIcon icon={faPlus} style={{ visibility: "hidden" }} className="me-1" />
              {getTypeIcon(node.node.data.type)}
            </div>
          )}
          {haschildren && node.node.isClosed && (
            <div className="hstack" onClick={(e) => { e.stopPropagation(); node.node.toggle(); }}>
              <FontAwesomeIcon icon={faPlus} className="text-primary me-1" />
              {getTypeIcon(node.node.data.type)}
            </div>
          )}
          {haschildren && node.node.isOpen && (
            <div className="hstack" onClick={(e) => { e.stopPropagation(); node.node.toggle(); }}>
              <FontAwesomeIcon icon={faMinus} className="text-primary me-1" />
              {getTypeIcon(node.node.data.type)}
            </div>
          )}
        </div>
        <div className={styles.treeNodeLabel} title={node.node.data.name}>
          {matchIds.includes(node.node.id)
            ? <mark className={node.node.id === currentMatch ? styles.searchMatchCurrent : styles.searchMatch}>{TreeHelper.getDisplayName(node.node.data.name)}</mark>
            : TreeHelper.getDisplayName(node.node.data.name)}
          {matchIds.includes(node.node.id) && node.node.data.alias && <small className="ms-2 text-muted">{node.node.data.alias}</small>}
        </div>
      </div>
    );
  };

  const addPageVersion = (pageid: string | null): void => {
    if (pageid && !isNaN(Number(pageid)) && treeRef.current) {
      const node = treeRef.current.get(pageid);

      if (node) {
        selectPage(pageid);
        EventBus.getInstance().publish(EventBus.Event.OnAddPageVersion, pageid);
      }
    }
  };

  const editMetaData = (pageid: string | null): void => {
    if (pageid) {
      nav(`/content/metadata/${pageid}`);
    }
  };

  const editKeyword = (pageid: string | null): void => {
    if (pageid) {
      nav(`/content/pagekeyword/${pageid}`);
    }
  };

  const editRedirects = (): void => {
    nav("/content/manageredirects");
  };

  const deletePage = async (pageid: string | null): Promise<void> => {
    if (pageid !== null && !isNaN(Number(pageid)) && contentSvcRef.current && treeRef.current) {
      const confirmed = await confirmation.requestConfirmation("Delete this page? All content and versions will be removed.<br /><b>This action cannot be undone.</b>", "Delete Page");
      const node = treeRef.current.get(pageid);
      const parentnode = node?.parent;

      if (confirmed) {
        const delres = await contentSvcRef.current.deletePage(Number(pageid));
        if (delres.Success) {
          actionEmitter.emit(EmitEvent.Result, true, delres.Message ?? "Page deleted");
          setPageData((prev) => removePageNode(prev, pageid));
          cacheRefresh.reload();
          if (parentnode && parentnode?.data?.type !== "lang") {
            selectPage(parentnode.id);
          } else {
            setSelectedNodeId(null);
            setSelectedNode(null);
            if (props.onPageSelected) {
              props.onPageSelected(null);
            }
          }
        } else {
          actionEmitter.emit(EmitEvent.Result, false, delres.Message ?? "Failed to delete page");
        }
      }
    }
  };

  useImperativeHandle(ref, () => ({
    getSitePages,
    selectPage
  }));

  useEffect(() => {

    if (!siteSvcRef.current) {
      siteSvcRef.current = new SiteService();
    }

    const updateTreeHeight = () => {
      const height = window.innerHeight - heightdiff;
      setTreeHeight(height);
    }

    updateTreeHeight();
    window.addEventListener('resize', updateTreeHeight);
  }, []);

  useEffect(() => {
    if (!contentSvcRef.current) {
      contentSvcRef.current = new ContentService();
    }

    const getData = async () => {
      try {
        await Promise.all([
          getSitePages()
        ]);

        EventBus.getInstance().subscribe(EventBus.Event.OnTreeNodeSelect, selectPage);

      } catch (error) {
        console.log(error);
      }
    }

    getData();

    return () => {
      EventBus.getInstance().unsubscribe(EventBus.Event.OnTreeNodeSelect, selectPage);
    }
  }, []);

  useEffect(() => {
    if (selectedNodeId !== null && treeRef.current) {
      const node = treeRef.current.get(selectedNodeId.toString());

      if (node) {
        expandTreeToNode(selectedNodeId.toString());
        node.select(); // Select the newly added node
      }
    }
  }, [pageData, selectedNodeId]);

  useEffect(() => {
    if (!searchQuery.trim() || !matchIds.length) return;
    const frame = requestAnimationFrame(() => {
      for (const id of matchIds) treeRef.current?.openParents(id);
      void treeRef.current?.scrollTo(currentMatch, 'center');
    });
    return () => cancelAnimationFrame(frame);
  }, [matchIds, currentMatch, searchQuery]);

  return (
    <>
      <div className={styles.treePanel} ref={compRef}>
        <div className={styles.panelHeader}>
          <div className={styles.panelTitleWrap}>
            <span className={styles.panelTitle}>Your Site Pages</span>
          </div>
          <div className="d-flex flex-column align-items-end gap-2 ms-auto">
            <Button 
              type="button" 
              variant="outline-primary"
              className={`btn-nomin ${styles.treeActionButton}`}
              onClick={addPage}>
                <FontAwesomeIcon icon={faPlus} />
                <span>Add a New Page</span>
            </Button>
          <Button type="button" size="sm" variant="outline-secondary" className="align-self-end" disabled={loadingPages || reloadingTree}
            onClick={() => void reloadPageTree()}>
            <FontAwesomeIcon icon={faRetweet} spin={loadingPages || reloadingTree} className="me-2" />
            {reloadingTree ? 'Reloading page tree…' : 'Reload page tree'}
          </Button>
          </div>
        </div>
        <div className="px-3 pb-3">
          <FormControl type="search" value={searchQuery} placeholder="Search page titles or aliases…" aria-label="Search page titles or aliases"
            onChange={event => { setSearchQuery(event.target.value); setMatchIndex(0); }} />
          {searchQuery.trim() && <div className="d-flex align-items-center gap-2 mt-2">
            <span className="small" role="status" aria-live="polite">{matchIds.length ? `Match ${matchIndex % matchIds.length + 1} of ${matchIds.length}` : 'No matching pages.'}</span>
            <Button size="sm" variant="outline-primary" disabled={!matchIds.length} onClick={() => {
              const next = (matchIndex + 1) % matchIds.length;
              setMatchIndex(next);
              treeRef.current?.openParents(matchIds[next]);
              void treeRef.current?.scrollTo(matchIds[next], 'center');
            }}>Next match</Button>
          </div>}
        </div>
        <div className={styles.panelDivider} aria-hidden="true"></div>
        <div id="PagesTree" ref={treeBodyRef} className={`${styles.treeBody} ${styles.treeScroller}`} aria-busy={loadingPages}>
          {loadingPages && (
            <div className={styles.treeLoading} role="status" aria-live="polite">
              <FontAwesomeIcon icon={faCircleNotch} spin aria-hidden="true" />
              <span>Loading pages…</span>
            </div>
          )}
          <Tree
            ref={treeRef}
            data={pageData}
            openByDefault={false}
            height={treeHeight}
            width={"100%"}
            disableMultiSelection={true}
          >
            {nodeRender}
          </Tree>

          {/* Context Menu */}
          {ctxMenu.nodeId && (
            <>
              {createMenu()}
            </>
          )}

        </div>
      </div>

      {showAddPage && (
        <AppModal
          onClose={() => setShowAddPage(false)}
          showButtons={true}
          size="lg"
          title="Add Page"
          actionButtonVariant="success"
          cancelButtonVariant="primary"
          cancelButtonText="Cancel"
          dataActionButtonText="Add Page"
          onDataAction={handlePageSave}>
          <AddPageDialogue SelectedParent={addPageParentRef.current} ref={addPageRef} />
        </AppModal>
      )}
    </>
  );
});

export default PagesTree;
